import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Input, InputProps } from 'antd';
import { debounce } from 'lodash';

import { isNil } from '@/utils/number';
import IntlContext from '@/components/Intl/IntlContext';
import { getDecimalsDigits, isDecimalsFn } from './validator';

/**
 * 金额输入框
 */
interface InputMoneyProps extends Omit<InputProps, 'value' | 'onChange' | 'onBlur'> {
  requiredBlur?: boolean;
  myRef?: any;
  prefix?: string;
  value?: number | string;
  onChange?: (value: number | string) => void;
  onBlur?: (value: number | string) => void;
}

const ALL_0_REG = /^0+$/;
const REMOVE_PREFIX_0_REG = /^0+?([1-9]+|0\.)/;

const InputMoney: React.FC<InputMoneyProps> = ({
  requiredBlur = true,
  myRef,
  prefix,
  value,
  onChange,
  onBlur,
  ...rest
}) => {
  const [inputVal, setInputVal] = useState<string>();
  const isFirstComeinRef = useRef<boolean>(true);
  const intlCtx = useContext(IntlContext);
  const inputPrefix = prefix ?? intlCtx.TenantBaseConfig.CurrencySymbol;

  const handleChange = (val) => {
    if (onChange) {
      onChange?.(val);
    }
  };

  const handleInputChange = (e) => {
    const { value: tempValue } = e.target;
    setInputVal(tempValue);
    handleChange?.(tempValue);
  };

  // 小数位数自动补齐
  const fill2Digits = (val) => {
    let result = val;
    const { maxDecimalsDigits, numberReg } = isDecimalsFn();
    if (numberReg?.test(result) && getDecimalsDigits(result) < maxDecimalsDigits) {
      result = Number(result).toFixed(maxDecimalsDigits);
    }
    return result;
  };

  const save = () => {
    setInputVal(inputVal);
    let result = inputVal;
    // 将整数、小数最前面多余0去除
    if (ALL_0_REG.test(result)) {
      result = result.replace(ALL_0_REG, '0');
      handleChange?.(result);
    }
    if (REMOVE_PREFIX_0_REG.test(result)) {
      result = result.replace(REMOVE_PREFIX_0_REG, '$1');
      handleChange?.(result);
    }
    if (fill2Digits(result) !== result) {
      result = fill2Digits(result);
      handleChange?.(result);
    }
    onBlur?.(result);
  };

  useEffect(() => {
    let result = isNil(value) ? '' : String(value);
    // 第一次进来
    if (isFirstComeinRef.current) {
      isFirstComeinRef.current = false;
      result = fill2Digits(result);
    }
    // 非第一次渲染输入啥是啥
    setInputVal(result);
  }, [value]);

  useEffect(() => {
    return () => {
      isFirstComeinRef.current = true;
    };
  }, []);

  return (
    <>
      <Input
        autoComplete="off"
        ref={myRef}
        prefix={inputPrefix}
        value={inputVal}
        onChange={handleInputChange}
        onPressEnter={save}
        onBlur={requiredBlur ? save : () => {}}
        {...rest}
      />
    </>
  );
};

export default React.forwardRef<any, InputMoneyProps>((props, ref) => (
  <InputMoney {...props} myRef={ref} />
));
